import {RefObject, useCallback} from 'react';

import type {
    OnShippingOptionsChangeActions,
    OnShippingOptionsChangeData,
} from '@paypal/paypal-js/types';

import {saveShippingMethodApi} from 'api/custom';
import {handlePayPalDesktopErrorAnalytic} from 'features/expressPayments/payPalV3/utils/analytics';
import {logWithSentry} from 'libs/sentry/sentry';
import {ShippingMethodType} from 'types/shippingMethodsType';

import {OnShippingOptionChangeAdditionalType} from './types';
import {useGetAndUpdateTotals} from './useGetAndUpdateTotals';

/**
 * Handle paypal shipping options change
 */
export const useShippingOptionsChange = (shippingMethodsRef: RefObject<ShippingMethodType[]>) => {
    const getAndUpdateTotals = useGetAndUpdateTotals();

    const onShippingOptionsChange = useCallback(
        async (
            {selectedShippingOption}: OnShippingOptionsChangeData,
            actions: OnShippingOptionsChangeActions,
            additionalInfo: OnShippingOptionChangeAdditionalType,
        ) => {
            try {
                if (!selectedShippingOption?.id) {
                    logWithSentry(new Error('PayPal V3 shipping option is empty!'));
                    return;
                }
                await saveShippingMethodApi(selectedShippingOption.id);

                await getAndUpdateTotals(
                    additionalInfo.id,
                    shippingMethodsRef.current?.map(method => ({
                        ...method,
                        selected: method.code === selectedShippingOption.id,
                    })) || [],
                );
            } catch (error) {
                await actions.reject();
                handlePayPalDesktopErrorAnalytic(error);
                logWithSentry(error, 'PayPal V3 change shipping option error');
            }
        },
        [getAndUpdateTotals, shippingMethodsRef],
    );

    return onShippingOptionsChange;
};
